import { type HeadingProps } from "@client/controls/HTML/Heading/component";
import { globals } from "@client/globals";
import { widthToColumn } from "@client/lib/math";
import { joinDefined } from "@client/lib/misc";
import { getImage } from "@client/lib/settings";
import { STYLE_SHEET_IDS } from "@client/lib/styleSheet";
import {
  activePopupSelector,
  contextSelector,
  cultureCodeSelector,
  entitiesSelector,
  funnelInfoSelector,
  getCompositeSettingSelector,
  getSettingValueSelector,
  idPropsSelector,
  isEditModeSelector,
  labelsSelector,
  makeEntitySelector,
  makeModuleTypeSelector,
  makeTextStyleSelector,
  modulesSelector,
  pageIdSelector,
  pageVariantIdSelector,
  portalIdSelector,
  screenSelector,
  type SettingsParametricSelector,
  type SettingsPropsParametricSelector,
  styleSheetIdToTypeSelector,
  TargetSelector
} from "@client/selectors";
import {
  type AppState,
  type CompositeSettings,
  type Context,
  type GenericMap,
  type i18n,
  ScreenType
} from "@client/types";
import { type ApiInfo } from "@client/types/Api";
import { type DynamicForms } from "@common/controls/Form/DynamicForm/types";
import { merge } from "@common/lib/i18n";
import classNames from "classnames";
import { isUndefined } from "lodash";
import { createSelector } from "reselect";
import { getContentAssetUrl } from "./lib/assets";
import { getLabels } from "./lib/labels";
import { redirectTo as rt } from "./lib/settings";
import { type RootContainerProps } from "./types";

export const makeModuleStateSelector = <TState extends { status: string }>(
  defaultValue: TState,
  previewValue: TState
) => {
  return createSelector(
    [modulesSelector, idPropsSelector],
    (modules, id): TState => modules[id] ?? (globals.mode === "preview" ? previewValue : defaultValue)
  );
};

export const makeModuleI18nSelector = () => {
  const moduleTypeSelector = makeModuleTypeSelector();

  return createSelector([labelsSelector, moduleTypeSelector], (labels, moduleType) =>
    merge(getLabels(labels, moduleType))
  );
};

export const makeModuleItemsSelector = () => {
  const entitySelector = makeEntitySelector();
  return createSelector([entitySelector, entitiesSelector], (entity, entities) =>
    (entity.childrenIds || []).map((id) => entities[id])
  );
};

export const makeModuleItemSelector = makeEntitySelector;

export const rootContainerPropsSelector = (state: any, props: RootContainerProps) => props;

export const makeTextHeadingLevelSelector = (target: string, prefix?: string) => {
  const styleSheetHeadingSelector = getSettingValueSelector(target, "s_text", joinDefined(".", prefix, "text"));
  const customTextLevelSelector = getSettingValueSelector(target, "level", joinDefined(".", prefix, "text"));

  return createSelector(
    [styleSheetHeadingSelector, customTextLevelSelector, styleSheetIdToTypeSelector],
    (ssh, customLevel, styleSheetIdToType) => {
      if (ssh === STYLE_SHEET_IDS.custom) return customLevel;

      return styleSheetIdToType[ssh];
    }
  );
};

// for regular text settings
export const makeHeadingPropsSelector = (target: string, prefix?: string): SettingsPropsParametricSelector => {
  const levelSelector = makeTextHeadingLevelSelector(target, prefix);
  const textStyleSelector = makeTextStyleSelector(target, prefix);

  return createSelector(
    [levelSelector, textStyleSelector],
    (level, style): HeadingProps => ({
      level,
      style
    })
  );
};

// End of Item Selectors

export const makeImageSrcSelector = (target: string, name: string, prefix?: string) => {
  const settingSelector = getSettingValueSelector(target, name, prefix);
  return createSelector([settingSelector, isEditModeSelector], (setting, isEditMode): string =>
    getContentAssetUrl(getImage(setting, isEditMode))
  );
};

export const makeImageSizeSelector = (target: string, prefix?: string) =>
  getSettingValueSelector(target, "size", joinDefined(".", prefix, "image"));

export const makeRedirectToSelector = (target: string, prefix?: string) => {
  const settingSelector = getCompositeSettingSelector<CompositeSettings.Redirect>(target, "redirect", prefix);

  return createSelector([settingSelector, funnelInfoSelector, screenSelector], rt.createFromSetting);
};

export type ModuleApiInfo = ApiInfo & {
  baseUrl: string;
  i18n: i18n;
  baseParams: {
    path: string;
    context: Context;
    portalId: string;
    pageId: string | null;
    pageVariantId: string | null;
    moduleId: string;
    popupId: string | null;
    screenType: ScreenType;

    cultureCode: string | null;
  };
};

export const makeModuleApiInfoSelector = () => {
  const i18nSelector = makeModuleI18nSelector();

  return createSelector(
    [
      contextSelector,
      portalIdSelector,
      pageIdSelector,
      pageVariantIdSelector,
      activePopupSelector,
      idPropsSelector,
      screenSelector,
      cultureCodeSelector,
      i18nSelector
    ],
    (context, portalId, pageId, pageVariantId, activePopup, id, screen, cultureCode, i18n): ModuleApiInfo => ({
      baseUrl: globals.paths.baseUrl,
      i18n: i18n,
      baseParams: {
        context,
        portalId,
        pageId,
        pageVariantId,
        screenType: screen,
        moduleId: id,
        path: window.location.pathname,
        popupId: activePopup?.id ?? null,
        cultureCode
      },
      url: globals.paths.apiUrl
    })
  );
};

export type ConsentItem = {
  type: "text" | "checkbox";
  checked?: boolean;
  label: string;
  required?: boolean;
  visible?: boolean;
  width?: string;
};
export type ConsentSettings = {
  items: GenericMap<ConsentItem> | undefined;
  location: "above" | "below";
  title: string;
};
export type Consent = Omit<ConsentSettings, "items"> & {
  fields: DynamicForms.Field[];
};

const propsConsentFieldIdsSelector = (state: AppState, props: { consentFieldIds: string[] }) => props.consentFieldIds;
export const makeConsentSelector = () => {
  const consentSettingSelector = getCompositeSettingSelector<ConsentSettings>(TargetSelector.Entity, "consent");

  return createSelector(
    [consentSettingSelector, propsConsentFieldIdsSelector],
    (consentSetting, consentFieldIds): Consent | undefined => {
      if (!consentSetting) return;

      const items = consentSetting.items ?? {};
      return {
        location: consentSetting.location,
        title: consentSetting.title,
        fields: Object.keys(items)
          .filter((x) => consentFieldIds.some((y) => y.toLowerCase() === x.toLowerCase() || items[x].type === "text"))
          .map<DynamicForms.Field>((x, i) => {
            const item = items[x];

            const isCheckbox = item.type === "checkbox";
            const isVisible = isUndefined(item.visible) || item.visible;
            const label = item.label;
            const value = isCheckbox ? item.checked ?? false : label;

            return {
              formControlKey: isVisible ? (isCheckbox ? "Checkbox" : "PlainText") : "Hidden",
              label: isCheckbox ? label : undefined,
              name: x,
              isRequired: !isVisible ? false : item.required,
              sortOrder: i,
              width: widthToColumn(item.width || "100%"),
              value
            };
          })
      };
    }
  );
};

export const combinedClassNameSelector = (
  target: string,
  settings: string[],
  className?: string,
  prefix?: string
): SettingsParametricSelector<string> =>
  createSelector(
    settings.map((s) => getSettingValueSelector(target, s, prefix)),
    (...values) => classNames(...values, className)
  );
